import { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, GraduationCap, BookOpen, User, Home, LogOut,
  Mail, MapPin, Monitor, Tag, FileText, PlayCircle, Download, Upload,
  Video, Plus, Pencil, Trash2, CheckCircle,
  XCircle, AlertCircle, Clock, Calendar, ArrowRight, TrendingUp,
  Zap, Star, Lock, Eye, EyeOff, Link2, Unlink,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  fetchTutorById, fetchResources, createResource, deleteResource,
  fetchMyClasses, createClass, updateClass, deleteClass,
  getGoogleAuthUrl, disconnectGoogle, getResourceFileUrl,
  API_BASE_URL,
} from '../services/api';
import JoinClassButton from '../components/JoinClassButton';
import PdfViewerModal from '../components/PdfViewerModal';

// ── Constants ────────────────────────────────────────────────────────────────
const SECTIONS = [
  { key: 'dashboard', label: 'Dashboard',      icon: LayoutDashboard },
  { key: 'classes',   label: 'Online Classes', icon: Video           },
  { key: 'resources', label: 'Resources',      icon: BookOpen        },
  { key: 'profile',   label: 'My Profile',     icon: User            },
];

const PLATFORMS = ['Zoom', 'Google Meet', 'Google Classroom', 'Microsoft Teams', 'Other'];
const GOOGLE_PLATFORMS = ['Google Meet', 'Google Classroom'];

const PLATFORM_STYLE = {
  'Zoom':             'bg-blue-500/10 text-blue-400 border-blue-500/20',
  'Google Meet':      'bg-green-500/10 text-green-400 border-green-500/20',
  'Google Classroom': 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20',
  'Microsoft Teams':  'bg-purple-500/10 text-purple-400 border-purple-500/20',
  'Other':            'bg-white/10 text-gray-300 border-white/20',
};

const getSectionFromPath = (p) => {
  const parts = p.split('/').filter(Boolean);
  if (parts[0] !== 'teachers' || parts[1] !== 'admin') return 'dashboard';
  return parts[2] || 'dashboard';
};

const emptyForm = {
  title: '', subject: '', level: 'O/L', syllabus: 'Local',
  platform: 'Zoom', meetingLink: '', googleEventId: '', schedDate: '', schedTime: '', duration: 60, description: '',
};

const RESOURCE_CATEGORIES = ['Past Papers', 'Revision', 'Books/Notes', 'Short Notes'];
const emptyResourceForm = { title: '', category: 'Past Papers', syllabusType: 'Local', subject: '', year: '', medium: '' };

const fmtDate = (d) => new Date(d).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
const fmtTime = (d) => new Date(d).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });

const getGreeting = () => {
  const h = new Date().getHours();
  if (h < 12) return 'Good Morning';
  if (h < 17) return 'Good Afternoon';
  return 'Good Evening';
};

const todayStr = new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });

// ── Toast ──────────────────────────────────────────────────────────────────────
const Toast = ({ toast, onClose }) => (
  <AnimatePresence>
    {toast.visible && (
      <motion.div initial={{ opacity: 0, y: -40, scale: 0.9 }} animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 20, scale: 0.9 }} className="fixed top-5 right-5 z-[100]">
        <div className={`flex items-center gap-3 px-5 py-4 rounded-2xl border shadow-xl backdrop-blur-xl
          ${toast.type === 'success' ? 'bg-emerald-500/20 border-emerald-500/30 text-emerald-400' : 'bg-red-500/20 border-red-500/30 text-red-400'}`}>
          {toast.type === 'success' ? <CheckCircle size={18} /> : <XCircle size={18} />}
          <p className="text-sm font-semibold text-white">{toast.message}</p>
          <button onClick={onClose} className="ml-2 opacity-60 hover:opacity-100"><XCircle size={16} /></button>
        </div>
      </motion.div>
    )}
  </AnimatePresence>
);

// ── Confirm Modal ──────────────────────────────────────────────────────────────
const ConfirmModal = ({ open, message, onConfirm, onClose }) => (
  <AnimatePresence>
    {open && (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
        <motion.div initial={{ scale: 0.9, y: -20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, y: -20 }}
          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          className="bg-darkBg border border-white/10 rounded-3xl p-8 max-w-sm w-full shadow-2xl" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center gap-3 mb-5">
            <div className="p-2.5 bg-red-500/10 rounded-xl text-red-400"><AlertCircle size={22} /></div>
            <h3 className="font-bold text-lg text-white">Confirm Delete</h3>
          </div>
          <p className="text-gray-400 text-sm mb-8">{message}</p>
          <div className="flex gap-3">
            <button onClick={onClose} className="flex-1 py-2.5 rounded-xl font-bold text-sm bg-white/10 hover:bg-white/20 transition-colors">Cancel</button>
            <button onClick={onConfirm} className="flex-1 py-2.5 rounded-xl font-bold text-sm bg-red-500/20 text-red-400 border border-red-500/30 hover:bg-red-500/30 transition-colors">Delete</button>
          </div>
        </motion.div>
      </motion.div>
    )}
  </AnimatePresence>
);

// ── Class Card ─────────────────────────────────────────────────────────────────
const ClassCard = ({ cls, onEdit, onDelete, isPast = false, token, displayName }) => (
  <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
    className={`border rounded-2xl p-5 transition-all ${isPast ? 'bg-white/3 border-white/5 opacity-60' : 'bg-white/5 border-white/10 hover:border-purple-500/30'}`}>
    <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
      <div className="flex-1">
        <div className="flex flex-wrap gap-2 mb-2">
          <span className={`text-xs px-2.5 py-1 rounded-full border font-semibold ${PLATFORM_STYLE[cls.platform] || PLATFORM_STYLE['Other']}`}>{cls.platform}</span>
          <span className="text-xs px-2.5 py-1 bg-purple-500/10 text-purple-400 rounded-full">{cls.subject}</span>
          <span className="text-xs px-2.5 py-1 bg-white/10 text-gray-300 rounded-full">{cls.level}</span>
          <span className="text-xs px-2.5 py-1 bg-white/10 text-gray-300 rounded-full">{cls.syllabus}</span>
          {isPast && <span className="text-xs px-2.5 py-1 bg-white/5 text-gray-500 rounded-full">Ended</span>}
        </div>
        <h4 className="font-bold text-white text-base mb-2">{cls.title}</h4>
        <div className="flex flex-wrap gap-4 text-xs text-gray-400">
          <span className="flex items-center gap-1"><Calendar size={11} /> {fmtDate(cls.scheduledAt)}</span>
          <span className="flex items-center gap-1"><Clock size={11} /> {fmtTime(cls.scheduledAt)} · {cls.duration} min</span>
        </div>
        {cls.description && <p className="text-xs text-gray-500 mt-2 line-clamp-2">{cls.description}</p>}
      </div>
      <div className="flex sm:flex-col gap-2 shrink-0">
        <JoinClassButton
          classId={cls._id}
          token={token}
          displayName={displayName}
          disabled={isPast}
          className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold transition-colors ${isPast ? 'bg-white/5 text-gray-500 pointer-events-none' : 'bg-purple-600/20 border border-purple-500/30 text-purple-400 hover:bg-purple-600/30'}`}
        />
        <button onClick={() => onEdit(cls)} className="flex items-center gap-1.5 px-3 py-2 bg-white/5 border border-white/10 text-white rounded-xl text-xs font-bold hover:bg-accentBlue/10 hover:text-accentBlue hover:border-accentBlue/30 transition-colors">
          <Pencil size={12} /> Edit
        </button>
        <button onClick={() => onDelete(cls._id, cls.title)} className="flex items-center gap-1.5 px-3 py-2 bg-white/5 border border-white/10 text-white rounded-xl text-xs font-bold hover:bg-red-500/10 hover:text-red-400 hover:border-red-400/30 transition-colors">
          <Trash2 size={12} /> Delete
        </button>
      </div>
    </div>
  </motion.div>
);

// ── Main Component ─────────────────────────────────────────────────────────────
const TeacherAdminPage = () => {
  const navigate      = useNavigate();
  const location      = useLocation();
  const activeSection = getSectionFromPath(location.pathname);

  // ── Auth gate ────────────────────────────────────────────────────────────────
  const [isTeacherAuth, setIsTeacherAuth] = useState(() => {
    const u = JSON.parse(localStorage.getItem('teacherInfo') || '{}');
    return u.role === 'teacher';
  });
  const [loginForm, setLoginForm]               = useState({ usernameOrEmail: '', password: '' });
  const [loginError, setLoginError]             = useState('');
  const [loginLoading, setLoginLoading]         = useState(false);
  const [showLoginPassword, setShowLoginPassword] = useState(false);

  const handleTeacherLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    setLoginLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/tutors/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(loginForm),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Login failed.');
      if (data.role !== 'teacher') throw new Error('Access denied. Teacher credentials required.');
      localStorage.setItem('teacherInfo', JSON.stringify(data));
      setIsTeacherAuth(true);
    } catch (err) {
      setLoginError(err.message);
    } finally {
      setLoginLoading(false);
    }
  };

  const [teacher, setTeacher] = useState(() => JSON.parse(localStorage.getItem('teacherInfo') || '{}'));
  const [time, setTime]       = useState(new Date());

  // classes
  const [classes,        setClasses]       = useState([]);
  const [classForm,      setClassForm]     = useState(() => ({ ...emptyForm, subject: teacher.subjects?.[0]?.subjectName || '', syllabus: teacher.syllabus?.[0] || 'Local' }));
  const [editingClassId, setEditingClassId] = useState('');
  const [savingClass,    setSavingClass]   = useState(false);
  const [classFeedback,  setClassFeedback] = useState('');

  // resources
  const [resources,             setResources]             = useState([]);
  const [loadingResources,      setLoadingResources]      = useState(false);
  const [resourceSubjectFilter, setResourceSubjectFilter] = useState('');
  const [resourceForm,       setResourceForm]       = useState(() => ({
    ...emptyResourceForm,
    subject: teacher.subjects?.[0]?.subjectName || '',
    syllabusType: teacher.syllabus?.[0] || 'Local',
  }));
  const [resourceFile,       setResourceFile]       = useState(null);
  const [uploadingResource,  setUploadingResource]  = useState(false);
  const [resourceFeedback,   setResourceFeedback]   = useState('');
  const [previewResource,    setPreviewResource]    = useState(null);

  // ui
  const [toast,   setToast]   = useState({ visible: false, message: '', type: 'success' });
  const [confirm, setConfirm] = useState({ open: false, message: '', onConfirm: () => {} });

  const showToast = (msg, type = 'success') => {
    setToast({ visible: true, message: msg, type });
    setTimeout(() => setToast((t) => ({ ...t, visible: false })), 4000);
  };

  // live clock
  useEffect(() => {
    const t = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  // sync teacher state after auth
  useEffect(() => {
    if (isTeacherAuth) {
      const stored = JSON.parse(localStorage.getItem('teacherInfo') || '{}');
      setTeacher(stored);
    }
  }, [isTeacherAuth]);

  const teacherIdRef = teacher._id;

  // refresh profile
  useEffect(() => {
    if (!teacherIdRef) return;
    fetchTutorById(teacherIdRef).then((res) => {
      const fresh = res.data;
      setTeacher((prev) => {
        const updated = { ...prev, ...fresh };
        localStorage.setItem('userInfo', JSON.stringify(updated));
        return updated;
      });
      if (!editingClassId && (fresh.subjects || []).length > 0) {
        setClassForm((prev) => ({
          ...prev,
          subject:  prev.subject  || fresh.subjects[0].subjectName,
          syllabus: prev.syllabus || fresh.syllabus?.[0] || 'Local',
        }));
      }
    }).catch(console.error);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teacherIdRef]);

  // load classes (on mount + when entering classes section)
  const loadClasses = useCallback(() => {
    if (!teacherIdRef) return;
    fetchMyClasses(teacherIdRef).then((r) => setClasses(r.data)).catch(console.error);
  }, [teacherIdRef]);

  useEffect(() => { loadClasses(); }, [loadClasses]);

  useEffect(() => {
    if (activeSection === 'classes') loadClasses();
  }, [activeSection, loadClasses]);

  // load resources
  const loadResources = useCallback(() => {
    setLoadingResources(true);
    fetchResources().then((res) => {
      const mySubjects = (teacher.subjects || []).map((s) => s.subjectName.toLowerCase());
      setResources((res.data || []).filter((r) => r.subject && mySubjects.some((s) => r.subject.toLowerCase().includes(s))));
    }).catch(console.error).finally(() => setLoadingResources(false));
  }, [teacher.subjects]);

  useEffect(() => {
    if (activeSection === 'resources') loadResources();
  }, [activeSection, loadResources]);

  const goTo         = (key) => navigate(key === 'dashboard' ? '/teachers/admin' : `/teachers/admin/${key}`);
  const handleLogout = () => { localStorage.removeItem('teacherInfo'); setIsTeacherAuth(false); };

  // Google connect/disconnect (powers auto-created Meet links below)
  const [connectingGoogle, setConnectingGoogle] = useState(false);

  const handleConnectGoogle = async () => {
    setConnectingGoogle(true);
    try {
      const { data } = await getGoogleAuthUrl(teacher.token);
      window.location.href = data.url;
    } catch {
      showToast('Could not start Google connection.', 'error');
      setConnectingGoogle(false);
    }
  };

  const handleDisconnectGoogle = async () => {
    try {
      await disconnectGoogle(teacher.token);
      setTeacher((prev) => ({ ...prev, google: null }));
      showToast('Google account disconnected.');
    } catch {
      showToast('Failed to disconnect Google account.', 'error');
    }
  };

  // Pick up ?google=connected|error after the OAuth redirect lands back here
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const result = params.get('google');
    if (!result) return;
    if (result === 'connected') {
      showToast('Google account connected!');
      if (teacherIdRef) fetchTutorById(teacherIdRef).then((res) => setTeacher((prev) => ({ ...prev, ...res.data }))).catch(console.error);
    } else {
      showToast('Google connection failed. Please try again.', 'error');
    }
    navigate(location.pathname, { replace: true });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // class form handlers
  const resetClassForm = () => {
    setClassForm({ ...emptyForm, subject: (teacher.subjects || [])[0]?.subjectName || '', syllabus: (teacher.syllabus || [])[0] || 'Local' });
    setEditingClassId('');
    setClassFeedback('');
  };

  const handleClassSubmit = async (e) => {
    e.preventDefault();
    setClassFeedback('');
    const isGooglePlatform = GOOGLE_PLATFORMS.includes(classForm.platform);
    if (!classForm.title.trim())                            return setClassFeedback('Please enter a class title.');
    if (!isGooglePlatform && !classForm.meetingLink.trim())  return setClassFeedback('Please enter a meeting link.');
    if (isGooglePlatform && !editingClassId && !teacher.google?.email)
      return setClassFeedback('Connect your Google account below first so we can create a secure Meet link.');
    if (!classForm.schedDate)          return setClassFeedback('Please select a date.');
    if (!classForm.schedTime)          return setClassFeedback('Please select a time.');
    const scheduledAt = new Date(`${classForm.schedDate}T${classForm.schedTime}`);
    const payload = { ...classForm, duration: Number(classForm.duration) || 60, scheduledAt, teacherId: teacher._id, teacherName: teacher.firstName ? `${teacher.firstName} ${teacher.lastName}` : teacher.name };
    try {
      setSavingClass(true);
      if (editingClassId) {
        const res = await updateClass(editingClassId, payload);
        setClasses((prev) => prev.map((c) => (c._id === editingClassId ? res.data : c)));
        showToast('Class updated!');
      } else {
        const res = await createClass(payload);
        setClasses((prev) => [...prev, res.data]);
        showToast('Class scheduled!');
      }
      resetClassForm();
    } catch (err) {
      setClassFeedback(err?.response?.data?.message || 'Failed to save class.');
    } finally {
      setSavingClass(false);
    }
  };

  const handleEditClass = (cls) => {
    setEditingClassId(cls._id);
    setClassForm({
      title: cls.title, subject: cls.subject, level: cls.level, syllabus: cls.syllabus,
      platform: cls.platform, meetingLink: cls.meetingLink || '', googleEventId: cls.googleEventId || '',
      schedDate: cls.scheduledAt ? new Date(cls.scheduledAt).toISOString().slice(0, 10) : '',
      schedTime: cls.scheduledAt ? new Date(cls.scheduledAt).toTimeString().slice(0, 5) : '',
      duration: cls.duration, description: cls.description || '',
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDeleteClass = (id, title) => {
    setConfirm({
      open: true, message: `Delete "${title}"?`,
      onConfirm: async () => {
        setConfirm({ open: false });
        try { await deleteClass(id); setClasses((prev) => prev.filter((c) => c._id !== id)); showToast('Class deleted.'); }
        catch { showToast('Failed to delete.', 'error'); }
      },
    });
  };

  // resource handlers
  const handleResourceUpload = async (e) => {
    e.preventDefault();
    setResourceFeedback('');

    if (!resourceForm.title.trim() || !resourceForm.subject) {
      setResourceFeedback('Title and subject are required.');
      return;
    }
    if (!resourceFile) {
      setResourceFeedback('Please select a PDF file to upload.');
      return;
    }

    setUploadingResource(true);
    try {
      const fd = new FormData();
      fd.append('title', resourceForm.title.trim());
      fd.append('syllabusType', resourceForm.syllabusType);
      fd.append('category', resourceForm.category);
      fd.append('subject', resourceForm.subject);
      if (resourceForm.year) fd.append('year', resourceForm.year);
      if (resourceForm.medium) fd.append('medium', resourceForm.medium);
      fd.append('uploadedBy', 'teacher');
      fd.append('tutorId', teacher._id);
      fd.append('tutorName', teacher.firstName ? `${teacher.firstName} ${teacher.lastName || ''}`.trim() : teacher.name || 'Teacher');
      fd.append('file', resourceFile);

      await createResource(fd);
      setResourceForm(emptyResourceForm);
      setResourceFile(null);
      showToast('Resource uploaded successfully!');
      loadResources();
    } catch (err) {
      setResourceFeedback(err.response?.data?.message || 'Upload failed.');
    } finally {
      setUploadingResource(false);
    }
  };

  const handleDeleteResource = (id, title) => {
    setConfirm({
      open: true, message: `Delete "${title}"?`,
      onConfirm: async () => {
        setConfirm({ open: false });
        try { await deleteResource(id); setResources((prev) => prev.filter((r) => r._id !== id)); showToast('Resource deleted.'); }
        catch { showToast('Failed to delete.', 'error'); }
      },
    });
  };

  const upcomingClasses   = classes.filter((c) => new Date(c.scheduledAt) >= new Date()).sort((a, b) => new Date(a.scheduledAt) - new Date(b.scheduledAt));
  const pastClasses       = classes.filter((c) => new Date(c.scheduledAt) <  new Date());
  const nextClass         = upcomingClasses[0];
  const visibleResources  = resourceSubjectFilter ? resources.filter((r) => r.subject?.toLowerCase().includes(resourceSubjectFilter.toLowerCase())) : resources;

  const inputCls = 'w-full bg-darkBg border border-white/10 p-3.5 rounded-xl text-sm outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 transition-all';
  // True once we know the meeting link will be created/owned by Google (new
  // class on this platform, or an existing one already backed by a Calendar
  // event) — false for legacy Google Meet/Classroom classes with a manually
  // pasted link from before this feature existed, which stay editable.
  const isGoogleManaged = GOOGLE_PLATFORMS.includes(classForm.platform) && (!editingClassId || !!classForm.googleEventId);
  const teacherName = teacher.firstName ? `${teacher.firstName} ${teacher.lastName || ''}`.trim() : (teacher.name || 'Teacher');
  const teacherInitial = (teacher.firstName || teacher.name || 'T').charAt(0).toUpperCase();

  // ── Login gate ───────────────────────────────────────────────────────────────
  if (!isTeacherAuth) {
    return (
      <div className="min-h-screen bg-darkBg flex items-center justify-center px-4 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-[-10%] left-[-5%] w-[500px] h-[500px] bg-purple-500/5 rounded-full blur-[140px]" />
          <div className="absolute bottom-[-10%] right-[-5%] w-[400px] h-[400px] bg-purple-700/5 rounded-full blur-[120px]" />
        </div>

        <div className="w-full max-w-md relative z-10">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-purple-500/10 border border-purple-500/20 mb-5 shadow-lg shadow-purple-500/10">
              <GraduationCap size={30} className="text-purple-400" />
            </div>
            <div className="inline-flex items-center gap-1.5 px-3.5 py-1.5 bg-purple-500/10 border border-purple-500/20 rounded-full text-purple-400 text-[10px] font-bold uppercase tracking-[0.18em] mb-4">
              <Lock size={10} /> Teacher Portal
            </div>
            <h1 className="text-3xl font-extrabold text-white tracking-tight">Teacher Login</h1>
            <p className="text-gray-500 text-sm mt-2">Use the credentials provided by your admin.</p>
          </div>

          <div className="bg-white/4 border border-white/10 rounded-[28px] p-8 backdrop-blur-xl shadow-2xl">
            {loginError && (
              <div className="flex items-center gap-2.5 bg-red-500/10 border border-red-500/25 text-red-400 p-4 rounded-xl mb-6 text-sm">
                <AlertCircle size={15} className="shrink-0" />
                {loginError}
              </div>
            )}

            <form onSubmit={handleTeacherLogin} className="space-y-5">
              <div>
                <label className="block text-xs text-gray-400 mb-2 uppercase tracking-wider font-medium">Email or Username</label>
                <input
                  type="text"
                  value={loginForm.usernameOrEmail}
                  onChange={e => setLoginForm(p => ({ ...p, usernameOrEmail: e.target.value }))}
                  placeholder="username or email"
                  required
                  autoFocus
                  className="w-full bg-darkBg border border-white/10 p-3.5 rounded-xl text-sm outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 transition-all"
                />
              </div>

              <div>
                <label className="block text-xs text-gray-400 mb-2 uppercase tracking-wider font-medium">Password</label>
                <div className="relative">
                  <input
                    type={showLoginPassword ? 'text' : 'password'}
                    value={loginForm.password}
                    onChange={e => setLoginForm(p => ({ ...p, password: e.target.value }))}
                    placeholder="••••••••"
                    required
                    className="w-full bg-darkBg border border-white/10 p-3.5 pr-12 rounded-xl text-sm outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 transition-all"
                  />
                  <button
                    type="button"
                    onClick={() => setShowLoginPassword(v => !v)}
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white transition-colors"
                  >
                    {showLoginPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={loginLoading}
                className="w-full py-3.5 bg-purple-600 hover:bg-purple-500 text-white font-bold rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm mt-2"
              >
                {loginLoading ? (
                  <>
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Authenticating...
                  </>
                ) : (
                  <>
                    <GraduationCap size={16} /> Access Dashboard
                  </>
                )}
              </button>
            </form>
          </div>

          <p className="text-center text-gray-600 text-[11px] mt-6 tracking-wide">
            SMART EDU NEXT &mdash; Teacher Portal &mdash; Authorized personnel only.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full min-h-screen flex flex-col bg-darkBg">
      <Toast toast={toast} onClose={() => setToast((t) => ({ ...t, visible: false }))} />
      <ConfirmModal open={confirm.open} message={confirm.message} onConfirm={confirm.onConfirm} onClose={() => setConfirm({ open: false })} />

      {/* ── Navbar ── */}
      <nav className="flex items-center justify-between px-[5%] py-4 bg-purple-900/10 backdrop-blur-md border-b border-purple-500/15 sticky top-0 z-50">
        <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
          <div className="w-8 h-8 rounded-lg bg-purple-500/20 text-purple-400 flex items-center justify-center font-bold text-sm">{teacherInitial}</div>
          <span className="text-xl font-bold tracking-tighter text-purple-400">SMART EDU NEXT</span>
        </div>
        <div className="hidden md:flex flex-col items-center">
          <span className="text-lg font-bold text-white tracking-widest">{time.toLocaleTimeString()}</span>
          <span className="text-[11px] text-gray-400 uppercase tracking-wider">{time.toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' })}</span>
        </div>
        <div className="flex items-center gap-3">
          <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 bg-purple-500/10 border border-purple-500/20 rounded-xl">
            <div className="w-5 h-5 rounded-full bg-purple-500/30 text-purple-300 flex items-center justify-center text-[10px] font-bold">{teacherInitial}</div>
            <span className="text-xs font-semibold text-purple-300">{teacherName}</span>
            <span className="text-[10px] px-1.5 py-0.5 bg-purple-500/20 text-purple-400 rounded-full font-bold">Teacher</span>
          </div>
          <button onClick={() => navigate('/')} className="bg-white/10 hover:bg-white/20 text-white px-4 py-2 rounded-xl text-sm font-bold transition-colors flex items-center gap-2">
            <Home size={15} /><span className="hidden sm:block">Website</span>
          </button>
          <button onClick={handleLogout} className="bg-red-500/10 border border-red-500/30 text-red-400 text-sm font-bold px-4 py-2 rounded-xl hover:bg-red-500/20 transition-colors flex items-center gap-2">
            <LogOut size={15} /><span className="hidden sm:block">Logout</span>
          </button>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto w-full px-6 py-10">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">

          {/* ── Sidebar ── */}
          <aside className="lg:col-span-1 h-fit sticky top-24">
            <div className="bg-white/5 border border-white/10 rounded-3xl p-5 backdrop-blur-xl">
              {/* Teacher mini-card */}
              <div className="flex items-center gap-3 mb-6 p-4 bg-gradient-to-r from-purple-600/15 to-transparent border border-purple-500/15 rounded-2xl">
                <div className="w-12 h-12 rounded-2xl bg-purple-500/20 text-purple-400 flex items-center justify-center text-xl font-bold shrink-0">
                  {teacherInitial}
                </div>
                <div className="min-w-0">
                  <p className="font-bold text-white text-sm truncate">{teacherName}</p>
                  <p className="text-[11px] text-gray-400 truncate">{teacher.email}</p>
                  <span className="text-[10px] px-2 py-0.5 bg-purple-500/10 text-purple-400 rounded-full font-bold mt-1 inline-block">Teacher</span>
                </div>
              </div>

              <div className="space-y-1.5">
                {SECTIONS.map(({ key, label, icon: Icon }) => (
                  <button key={key} onClick={() => goTo(key)}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                      activeSection === key
                        ? 'bg-purple-500/10 border border-purple-500/30 text-purple-400 shadow-sm'
                        : 'border border-transparent text-gray-400 hover:bg-white/5 hover:text-white'
                    }`}>
                    <Icon size={16} />
                    <span>{label}</span>
                    {activeSection === key && <div className="ml-auto w-1.5 h-1.5 rounded-full bg-purple-400" />}
                  </button>
                ))}
              </div>

              {/* Upcoming class mini badge */}
              {nextClass && (
                <div className="mt-5 p-4 bg-green-500/5 border border-green-500/15 rounded-2xl">
                  <p className="text-[10px] text-green-400 font-bold uppercase tracking-wider mb-1.5 flex items-center gap-1">
                    <Zap size={10} /> Next Class
                  </p>
                  <p className="text-xs font-bold text-white truncate">{nextClass.title}</p>
                  <p className="text-[11px] text-gray-400 mt-0.5">{fmtDate(nextClass.scheduledAt)}</p>
                  <p className="text-[11px] text-green-400 font-semibold">{fmtTime(nextClass.scheduledAt)}</p>
                </div>
              )}
            </div>
          </aside>

          {/* ── Main Content ── */}
          <section className="lg:col-span-3 space-y-6">

            {/* ═══ DASHBOARD ═══ */}
            {activeSection === 'dashboard' && (
              <div className="space-y-6">

                {/* Welcome banner */}
                <div className="bg-gradient-to-r from-purple-600/20 via-purple-500/10 to-transparent border border-purple-500/20 rounded-3xl p-7">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-5">
                    <div>
                      <p className="text-sm text-purple-400 font-bold uppercase tracking-widest mb-1">
                        {getGreeting()}, {teacher.firstName || teacher.name || 'Teacher'} 👋
                      </p>
                      <h2 className="text-3xl font-extrabold text-white">Teacher Dashboard</h2>
                      <p className="text-gray-400 text-sm mt-1">{todayStr}</p>
                    </div>
                    <div className="flex gap-3 shrink-0">
                      <div className="bg-white/5 border border-white/10 rounded-2xl px-5 py-3 text-center min-w-[80px]">
                        <p className="text-2xl font-bold text-white">{(teacher.subjects || []).length}</p>
                        <p className="text-[11px] text-gray-400 mt-0.5">Subjects</p>
                      </div>
                      <div className="bg-purple-500/10 border border-purple-500/20 rounded-2xl px-5 py-3 text-center min-w-[80px]">
                        <p className="text-2xl font-bold text-purple-400">{upcomingClasses.length}</p>
                        <p className="text-[11px] text-gray-400 mt-0.5">Upcoming</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* KPI stat cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {[
                    { label: 'My Subjects',      value: (teacher.subjects || []).length, icon: GraduationCap, c: 'text-purple-400', b: 'bg-purple-400/10', key: 'profile'   },
                    { label: 'Upcoming Classes',  value: upcomingClasses.length,          icon: Video,         c: 'text-green-400',  b: 'bg-green-400/10',  key: 'classes'   },
                    { label: 'Past Classes',      value: pastClasses.length,              icon: Clock,         c: 'text-blue-400',   b: 'bg-blue-400/10',   key: 'classes'   },
                    { label: 'Resources',         value: resources.length || '—',         icon: BookOpen,      c: 'text-orange-400', b: 'bg-orange-400/10', key: 'resources' },
                  ].map(({ label, value, icon: Icon, c, b, key }) => (
                    <button key={label} onClick={() => goTo(key)}
                      className="bg-white/5 border border-white/10 rounded-2xl p-5 hover:border-white/20 hover:-translate-y-1 transition-all duration-300 group text-left w-full">
                      <div className={`p-2.5 rounded-xl ${b} ${c} w-fit mb-4 group-hover:scale-110 transition-transform`}>
                        <Icon size={18} />
                      </div>
                      <p className="text-3xl font-extrabold text-white">{value}</p>
                      <p className="text-xs text-gray-400 mt-1">{label}</p>
                      <div className={`flex items-center gap-1 mt-2 text-[10px] font-semibold ${c} opacity-70`}>
                        <TrendingUp size={10} /> View
                      </div>
                    </button>
                  ))}
                </div>

                {/* 2-col: classes + actions */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">

                  {/* Upcoming classes mini-list */}
                  <div className="md:col-span-2 bg-white/5 border border-white/10 rounded-3xl p-6">
                    <div className="flex items-center justify-between mb-5">
                      <h3 className="font-bold text-base flex items-center gap-2">
                        <Calendar size={16} className="text-green-400" /> Upcoming Classes
                      </h3>
                      <button onClick={() => goTo('classes')} className="text-xs text-purple-400 hover:underline flex items-center gap-1">
                        Schedule <ArrowRight size={11} />
                      </button>
                    </div>
                    {upcomingClasses.length === 0 ? (
                      <div className="py-8 text-center">
                        <Video size={32} className="text-gray-600 mx-auto mb-3" />
                        <p className="text-gray-500 text-sm">No upcoming classes. Schedule one now!</p>
                        <button onClick={() => goTo('classes')}
                          className="mt-4 px-4 py-2 bg-purple-600/20 border border-purple-500/30 text-purple-400 rounded-xl text-xs font-bold hover:bg-purple-600/30 transition-colors">
                          + Schedule Class
                        </button>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {upcomingClasses.slice(0, 3).map((cls) => (
                          <div key={cls._id} className="flex items-center gap-4 p-3 bg-darkBg rounded-2xl border border-white/5 hover:border-purple-500/20 transition-colors">
                            <div className="w-10 h-10 rounded-xl bg-purple-500/10 text-purple-400 flex items-center justify-center shrink-0">
                              <Video size={16} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-semibold text-sm text-white truncate">{cls.title}</p>
                              <p className="text-xs text-gray-400">{fmtDate(cls.scheduledAt)} · {fmtTime(cls.scheduledAt)}</p>
                            </div>
                            <div className="flex flex-col items-end gap-1 shrink-0">
                              <span className={`text-[10px] px-2 py-0.5 rounded-full border font-semibold ${PLATFORM_STYLE[cls.platform] || PLATFORM_STYLE['Other']}`}>{cls.platform}</span>
                              <JoinClassButton
                                classId={cls._id}
                                token={teacher.token}
                                displayName={teacher.firstName}
                                className="text-[10px] text-purple-400 hover:underline flex items-center gap-0.5 bg-transparent p-0"
                              />
                            </div>
                          </div>
                        ))}
                        {upcomingClasses.length > 3 && (
                          <p className="text-center text-xs text-gray-500">+{upcomingClasses.length - 3} more upcoming</p>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Quick actions + profile summary */}
                  <div className="space-y-4">
                    {/* Quick actions */}
                    <div className="bg-white/5 border border-white/10 rounded-3xl p-5">
                      <h3 className="font-bold text-sm mb-4 text-white">Quick Actions</h3>
                      <div className="space-y-2">
                        {[
                          { label: 'Schedule Class',  key: 'classes',   icon: Video,         color: 'text-purple-400' },
                          { label: 'View Resources',  key: 'resources', icon: BookOpen,      color: 'text-orange-400' },
                          { label: 'My Profile',      key: 'profile',   icon: User,          color: 'text-cyan-400'   },
                        ].map((a) => (
                          <button key={a.key} onClick={() => goTo(a.key)}
                            className="w-full flex items-center gap-3 px-3 py-2.5 bg-darkBg border border-white/5 rounded-xl hover:border-white/15 hover:bg-white/5 transition-all group text-left">
                            <a.icon size={14} className={`${a.color} group-hover:scale-110 transition-transform shrink-0`} />
                            <span className="text-xs text-gray-300 group-hover:text-white transition-colors flex-1">{a.label}</span>
                            <ArrowRight size={12} className="text-gray-600 group-hover:text-gray-400 transition-colors" />
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Teaching info */}
                    <div className="bg-white/5 border border-white/10 rounded-3xl p-5">
                      <h3 className="font-bold text-sm mb-4 text-white flex items-center gap-2"><Star size={13} className="text-yellow-400" /> Teaching Info</h3>
                      <div className="space-y-3">
                        <div className="flex items-center gap-2">
                          <Monitor size={13} className="text-purple-400 shrink-0" />
                          <span className="text-xs text-gray-400">Mode:</span>
                          <span className="text-xs text-white font-semibold">{teacher.mode || '—'}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <MapPin size={13} className="text-purple-400 shrink-0" />
                          <span className="text-xs text-gray-400">Area:</span>
                          <span className="text-xs text-white font-semibold truncate">{teacher.area || '—'}</span>
                        </div>
                        <div>
                          <p className="text-xs text-gray-400 mb-1.5">Syllabus:</p>
                          <div className="flex flex-wrap gap-1">
                            {(teacher.syllabus || []).map((s) => (
                              <span key={s} className="text-[10px] px-2 py-0.5 bg-purple-500/10 text-purple-400 rounded-full font-semibold">{s}</span>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Subjects overview */}
                {(teacher.subjects || []).length > 0 && (
                  <div className="bg-white/5 border border-white/10 rounded-3xl p-6">
                    <div className="flex items-center justify-between mb-5">
                      <h3 className="font-bold text-base flex items-center gap-2">
                        <GraduationCap size={16} className="text-purple-400" /> My Subjects
                      </h3>
                      <button onClick={() => goTo('profile')} className="text-xs text-purple-400 hover:underline flex items-center gap-1">
                        Full profile <ArrowRight size={11} />
                      </button>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {(teacher.subjects || []).map((s, i) => (
                        <div key={i} className="flex items-center gap-4 p-4 bg-darkBg rounded-2xl border border-white/5 hover:border-purple-500/20 transition-colors">
                          <div className="w-10 h-10 rounded-xl bg-purple-500/10 text-purple-400 flex items-center justify-center font-bold text-base shrink-0">
                            {s.subjectName.charAt(0)}
                          </div>
                          <div>
                            <p className="font-bold text-white text-sm">{s.subjectName}</p>
                            <div className="flex gap-1.5 mt-1">
                              {s.levels.map((l) => (
                                <span key={l} className="text-[10px] px-2 py-0.5 bg-purple-500/10 text-purple-400 rounded-full font-semibold">{l}</span>
                              ))}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ═══ ONLINE CLASSES ═══ */}
            {activeSection === 'classes' && (
              <>
                <div className="bg-white/5 border border-white/10 rounded-3xl p-6">
                  <h3 className="text-xl font-bold mb-1 text-white flex items-center gap-2">
                    <Video size={20} className="text-purple-400" />
                    {editingClassId ? 'Edit Class' : 'Schedule New Class'}
                  </h3>
                  <p className="text-gray-400 text-sm mb-6">Share a Zoom link, or let us auto-create a Google Meet/Classroom link only logged-in students can open.</p>
                  <form onSubmit={handleClassSubmit} className="space-y-4">
                    <input value={classForm.title} onChange={(e) => setClassForm((p) => ({ ...p, title: e.target.value }))}
                      placeholder="Class title (e.g. Combined Maths – Integration)" className={inputCls} />
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <select value={classForm.subject} onChange={(e) => setClassForm((p) => ({ ...p, subject: e.target.value }))} className={inputCls}>
                        <option value="">Select Subject</option>
                        {(teacher.subjects || []).map((s) => <option key={s.subjectName} value={s.subjectName}>{s.subjectName}</option>)}
                      </select>
                      <select value={classForm.level} onChange={(e) => setClassForm((p) => ({ ...p, level: e.target.value }))} className={inputCls}>
                        <option value="O/L">O/L</option>
                        <option value="A/L">A/L</option>
                      </select>
                      <select value={classForm.syllabus} onChange={(e) => setClassForm((p) => ({ ...p, syllabus: e.target.value }))} className={inputCls}>
                        {(teacher.syllabus || ['Local']).map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <select value={classForm.platform} onChange={(e) => setClassForm((p) => ({ ...p, platform: e.target.value }))} className={inputCls}>
                        {PLATFORMS.map((pl) => <option key={pl} value={pl}>{pl}</option>)}
                      </select>
                      {isGoogleManaged ? (
                        <input value={classForm.meetingLink} readOnly disabled
                          placeholder="Created automatically when you save" className={`${inputCls} opacity-60 cursor-not-allowed`} />
                      ) : (
                        <input value={classForm.meetingLink} onChange={(e) => setClassForm((p) => ({ ...p, meetingLink: e.target.value }))}
                          placeholder="Meeting link (https://...)" className={inputCls} />
                      )}
                    </div>

                    {GOOGLE_PLATFORMS.includes(classForm.platform) && (
                      <div className="bg-white/5 border border-white/10 rounded-2xl p-4 flex flex-wrap items-center justify-between gap-3">
                        {teacher.google?.email ? (
                          <>
                            <div className="flex items-center gap-2.5 text-sm text-emerald-400">
                              <Link2 size={16} />
                              <span>Google connected as <strong className="text-white">{teacher.google.email}</strong></span>
                            </div>
                            <button type="button" onClick={handleDisconnectGoogle}
                              className="flex items-center gap-1.5 text-xs font-semibold text-gray-400 hover:text-red-400 transition-colors">
                              <Unlink size={13} /> Disconnect
                            </button>
                          </>
                        ) : (
                          <>
                            <p className="text-sm text-gray-400">Connect your Google account to auto-create a secure Meet link for this class.</p>
                            <button type="button" onClick={handleConnectGoogle} disabled={connectingGoogle}
                              className="flex items-center gap-1.5 bg-white text-darkBg text-xs font-bold px-3.5 py-2 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50">
                              <Link2 size={13} /> {connectingGoogle ? 'Redirecting…' : 'Connect Google Account'}
                            </button>
                          </>
                        )}
                      </div>
                    )}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-xs text-gray-400 mb-1.5 uppercase tracking-wider">Date</label>
                        <input type="date" value={classForm.schedDate} min={new Date().toISOString().slice(0, 10)}
                          onChange={(e) => setClassForm((p) => ({ ...p, schedDate: e.target.value }))}
                          onClick={(e) => e.target.showPicker?.()} className={inputCls} />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-400 mb-1.5 uppercase tracking-wider">Time</label>
                        <input type="time" value={classForm.schedTime}
                          onChange={(e) => setClassForm((p) => ({ ...p, schedTime: e.target.value }))}
                          onClick={(e) => e.target.showPicker?.()} className={inputCls} />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-400 mb-1.5 uppercase tracking-wider">Duration (min)</label>
                        <input type="number" min="15" max="480" value={classForm.duration}
                          onChange={(e) => setClassForm((p) => ({ ...p, duration: e.target.value }))} className={inputCls} />
                      </div>
                    </div>
                    <textarea value={classForm.description} onChange={(e) => setClassForm((p) => ({ ...p, description: e.target.value }))}
                      placeholder="Notes for students (optional)" rows={3} className={`${inputCls} resize-none`} />
                    {classFeedback && <p className="text-red-400 text-sm flex items-center gap-1.5"><AlertCircle size={14} />{classFeedback}</p>}
                    <div className="flex gap-3">
                      <button type="submit" disabled={savingClass}
                        className="bg-purple-600 hover:bg-purple-500 text-white py-3.5 px-8 rounded-xl font-bold text-sm flex items-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                        {editingClassId ? <Pencil size={15} /> : <Plus size={15} />}
                        {savingClass ? 'Saving…' : editingClassId ? 'Update Class' : 'Schedule Class'}
                      </button>
                      {editingClassId && (
                        <button type="button" onClick={resetClassForm} className="bg-white/10 text-white py-3.5 px-8 rounded-xl font-bold text-sm hover:bg-white/20 transition-colors">Cancel</button>
                      )}
                    </div>
                  </form>
                </div>

                <div>
                  <h4 className="font-bold text-white mb-4 flex items-center gap-2">
                    <Calendar size={15} className="text-green-400" /> Upcoming
                    <span className="px-2 py-0.5 bg-green-500/10 text-green-400 text-xs rounded-full">{upcomingClasses.length}</span>
                  </h4>
                  {upcomingClasses.length === 0 ? (
                    <div className="py-10 text-center bg-white/5 border border-dashed border-white/15 rounded-3xl text-gray-500 text-sm">No upcoming classes scheduled.</div>
                  ) : (
                    <div className="space-y-4">
                      {upcomingClasses.map((cls) => <ClassCard key={cls._id} cls={cls} onEdit={handleEditClass} onDelete={handleDeleteClass} token={teacher.token} displayName={teacher.firstName} />)}
                    </div>
                  )}
                </div>

                {pastClasses.length > 0 && (
                  <div>
                    <h4 className="font-bold text-gray-400 mb-4 flex items-center gap-2">
                      <Clock size={15} /> Past Classes
                      <span className="px-2 py-0.5 bg-white/10 text-gray-400 text-xs rounded-full">{pastClasses.length}</span>
                    </h4>
                    <div className="space-y-4">
                      {pastClasses.map((cls) => <ClassCard key={cls._id} cls={cls} onEdit={handleEditClass} onDelete={handleDeleteClass} isPast token={teacher.token} displayName={teacher.firstName} />)}
                    </div>
                  </div>
                )}
              </>
            )}

            {/* ═══ RESOURCES ═══ */}
            {activeSection === 'resources' && (
              <>
                <div className="bg-white/5 border border-white/10 rounded-3xl p-6 mb-6">
                  <h3 className="text-lg font-bold text-white mb-4">Upload Resource</h3>
                  <form onSubmit={handleResourceUpload} className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <input
                      value={resourceForm.title}
                      onChange={(e) => setResourceForm((prev) => ({ ...prev, title: e.target.value }))}
                      placeholder="Resource title"
                      className="bg-darkBg border border-white/10 p-3.5 rounded-xl text-sm outline-none focus:border-purple-500 transition-all md:col-span-3"
                    />
                    <select
                      value={resourceForm.category}
                      onChange={(e) => setResourceForm((prev) => ({ ...prev, category: e.target.value }))}
                      className="bg-darkBg border border-white/10 p-3.5 rounded-xl text-sm outline-none focus:border-purple-500 transition-all"
                    >
                      {RESOURCE_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                    <select
                      value={resourceForm.syllabusType}
                      onChange={(e) => setResourceForm((prev) => ({ ...prev, syllabusType: e.target.value }))}
                      className="bg-darkBg border border-white/10 p-3.5 rounded-xl text-sm outline-none focus:border-purple-500 transition-all"
                    >
                      {(teacher.syllabus || ['Local']).map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <select
                      value={resourceForm.subject}
                      onChange={(e) => setResourceForm((prev) => ({ ...prev, subject: e.target.value }))}
                      className="bg-darkBg border border-white/10 p-3.5 rounded-xl text-sm outline-none focus:border-purple-500 transition-all"
                    >
                      <option value="">Select Subject</option>
                      {(teacher.subjects || []).map((s) => <option key={s.subjectName} value={s.subjectName}>{s.subjectName}</option>)}
                    </select>
                    <input
                      value={resourceForm.year}
                      onChange={(e) => setResourceForm((prev) => ({ ...prev, year: e.target.value }))}
                      placeholder="Year (optional)"
                      className="bg-darkBg border border-white/10 p-3.5 rounded-xl text-sm outline-none focus:border-purple-500 transition-all"
                    />
                    <select
                      value={resourceForm.medium}
                      onChange={(e) => setResourceForm((prev) => ({ ...prev, medium: e.target.value }))}
                      className="bg-darkBg border border-white/10 p-3.5 rounded-xl text-sm outline-none focus:border-purple-500 transition-all"
                    >
                      <option value="">Medium (optional)</option>
                      <option value="Sinhala">Sinhala</option>
                      <option value="English">English</option>
                      <option value="Tamil">Tamil</option>
                    </select>
                    <label className="flex items-center gap-2 bg-darkBg border border-white/10 p-3.5 rounded-xl text-sm cursor-pointer hover:border-purple-500/50 transition-all md:col-span-2">
                      <Upload size={16} className="text-purple-400 shrink-0" />
                      <span className="truncate text-gray-300">{resourceFile ? resourceFile.name : 'Choose PDF file'}</span>
                      <input type="file" accept="application/pdf" onChange={(e) => setResourceFile(e.target.files?.[0] || null)} className="hidden" />
                    </label>
                    <button
                      type="submit"
                      disabled={uploadingResource}
                      className="bg-purple-600 text-white rounded-xl font-bold text-sm flex items-center justify-center gap-2 hover:bg-purple-700 transition-colors disabled:opacity-50"
                    >
                      <Plus size={16} /> {uploadingResource ? 'Uploading...' : 'Upload'}
                    </button>
                    {resourceFeedback && <p className="md:col-span-3 text-sm font-medium text-red-400">{resourceFeedback}</p>}
                  </form>
                </div>

                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div>
                    <h3 className="text-xl font-bold text-white">Resources</h3>
                    <p className="text-gray-400 text-sm">Resources related to your subjects</p>
                  </div>
                  {(teacher.subjects || []).length > 0 && (
                    <select value={resourceSubjectFilter} onChange={(e) => setResourceSubjectFilter(e.target.value)}
                      className="bg-darkBg border border-white/10 p-3 rounded-xl text-sm outline-none focus:border-purple-500 transition-all">
                      <option value="">All My Subjects</option>
                      {(teacher.subjects || []).map((s) => <option key={s.subjectName} value={s.subjectName}>{s.subjectName}</option>)}
                    </select>
                  )}
                </div>
                {loadingResources ? (
                  <div className="py-16 text-center text-gray-500">Loading resources…</div>
                ) : visibleResources.length === 0 ? (
                  <div className="py-16 text-center bg-white/5 border border-dashed border-white/15 rounded-3xl text-gray-500 text-sm">No resources found for your subjects.</div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                    {visibleResources.map((item) => (
                      <div key={item._id} className="bg-white/5 border border-white/10 p-5 rounded-2xl hover:border-purple-500/20 transition-all flex flex-col">
                        <div className="flex justify-between items-start mb-4">
                          <div className="p-3 bg-purple-500/10 rounded-xl text-purple-400">
                            {item.category?.toLowerCase().includes('video') ? <PlayCircle size={20} /> : <FileText size={20} />}
                          </div>
                          {item.isPremium && <span className="text-[10px] bg-yellow-500/20 text-yellow-400 px-2 py-1 rounded-full font-bold uppercase">Premium</span>}
                        </div>
                        <h4 className="font-bold text-white mb-1 flex-1 text-sm">{item.title}</h4>
                        <p className="text-xs text-gray-400 mb-3">{item.subject}{item.year ? ` | ${item.year}` : ''}</p>
                        <div className="flex gap-2 mb-4 flex-wrap">
                          {item.category && <span className="text-xs px-2.5 py-1 bg-white/10 rounded-full text-gray-300">{item.category}</span>}
                          {item.syllabusType && <span className="text-xs px-2.5 py-1 bg-purple-500/10 rounded-full text-purple-400">{item.syllabusType}</span>}
                          {item.uploadedBy === 'admin' && <span className="text-xs px-2.5 py-1 bg-accentBlue/10 rounded-full text-accentBlue">Admin upload</span>}
                        </div>
                        <div className="flex gap-2 mt-auto">
                          {item.fileUrl ? (
                            <>
                              <button
                                type="button"
                                onClick={() => setPreviewResource(item)}
                                className="flex flex-1 items-center justify-center gap-2 rounded-xl border border-purple-500/30 bg-purple-600/20 py-2.5 text-sm font-bold text-purple-400 transition-colors hover:bg-purple-600/30"
                              >
                                <Eye size={14} /> View PDF
                              </button>
                              <a
                                href={getResourceFileUrl(item._id, true)}
                                className="flex items-center justify-center rounded-xl border border-white/10 bg-white/5 px-3 py-2.5 text-gray-300 transition-colors hover:bg-white/10 hover:text-white"
                                aria-label={`Download ${item.title}`}
                                title="Download PDF"
                              >
                                <Download size={14} />
                              </a>
                            </>
                          ) : (
                            <a
                              href={item.videoUrl || '#'}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex flex-1 items-center justify-center gap-2 rounded-xl border border-purple-500/30 bg-purple-600/20 py-2.5 text-sm font-bold text-purple-400 transition-colors hover:bg-purple-600/30"
                            >
                              <PlayCircle size={14} /> View
                            </a>
                          )}
                          {item.uploadedBy === 'teacher' && item.tutor === teacher._id && (
                            <button
                              onClick={() => handleDeleteResource(item._id, item.title)}
                              className="px-3 py-2.5 bg-red-500/10 border border-red-500/30 text-red-400 rounded-xl hover:bg-red-500/20 transition-colors"
                            >
                              <Trash2 size={14} />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <PdfViewerModal resource={previewResource} onClose={() => setPreviewResource(null)} />
              </>
            )}

            {/* ═══ PROFILE ═══ */}
            {activeSection === 'profile' && (
              <div className="space-y-5">
                <div className="bg-gradient-to-r from-purple-600/20 to-purple-900/5 border border-purple-500/20 rounded-3xl p-8 flex flex-col sm:flex-row items-center sm:items-start gap-6">
                  <div className="w-20 h-20 rounded-2xl bg-purple-500/20 text-purple-400 flex items-center justify-center text-4xl font-bold shrink-0 border border-purple-500/20">
                    {teacherInitial}
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-white">{teacherName}</h2>
                    <div className="flex flex-wrap gap-2 mt-2">
                      <span className="px-3 py-1 bg-purple-500/10 border border-purple-500/20 text-purple-400 text-xs font-bold rounded-full uppercase tracking-wider">Teacher</span>
                      {teacher.isPaidAd && <span className="px-3 py-1 bg-yellow-500/10 border border-yellow-500/20 text-yellow-400 text-xs font-bold rounded-full uppercase">Featured</span>}
                      {(teacher.syllabus || []).map((s) => <span key={s} className="px-3 py-1 bg-white/10 text-gray-300 text-xs font-semibold rounded-full">{s}</span>)}
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {[
                    { icon: Mail, label: 'Email', value: teacher.email },
                    { icon: MapPin, label: 'Area', value: teacher.area },
                    { icon: Monitor, label: 'Teaching Mode', value: teacher.mode },
                    { icon: GraduationCap, label: 'Syllabus', value: (teacher.syllabus || []).join(', ') || '—' },
                  ].map(({ icon: Icon, label, value }) => (
                    <div key={label} className="bg-white/5 border border-white/10 rounded-2xl p-5 flex items-center gap-4">
                      <div className="p-2.5 rounded-xl bg-purple-500/10 text-purple-400 shrink-0"><Icon size={17} /></div>
                      <div>
                        <p className="text-xs text-gray-400 mb-0.5">{label}</p>
                        <p className="font-semibold text-white text-sm">{value || '—'}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
                  <p className="text-xs text-gray-400 mb-3 flex items-center gap-1.5"><GraduationCap size={12} /> Teaching Subjects</p>
                  {(teacher.subjects || []).length === 0 ? <p className="text-gray-500 text-sm">No subjects assigned.</p> : (
                    <div className="flex flex-wrap gap-2">
                      {(teacher.subjects || []).map((s, i) => (
                        <span key={i} className="px-3 py-1.5 bg-purple-500/10 border border-purple-500/20 text-purple-400 rounded-full text-xs font-semibold">
                          {s.subjectName} ({s.levels.join(', ')})
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                {(teacher.tags || []).length > 0 && (
                  <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
                    <p className="text-xs text-gray-400 mb-3 flex items-center gap-1.5"><Tag size={12} /> Tags</p>
                    <div className="flex flex-wrap gap-2">
                      {teacher.tags.map((t, i) => <span key={i} className="px-3 py-1 bg-white/10 text-gray-300 rounded-full text-xs">{t}</span>)}
                    </div>
                  </div>
                )}
              </div>
            )}

          </section>
        </div>
      </div>
    </div>
  );
};

export default TeacherAdminPage;
